import { useEffect, useState } from "react";
import api from "@/lib/api";
import SEO from "@/components/SEO";

const DEFAULTS = {
    about_hero_title: "Events made unforgettable.",
    about_hero_subtitle: "X-Plosiv Event & Promotions brings people, brands and big ideas together through considered event planning and production.",
    about_hero_image: "/banner3.jpg",
    about_story_title: "We make moments matter.",
    about_story_body: "From corporate gatherings and weddings to exhibitions, brand activations and virtual experiences, our team brings creative thinking and meticulous coordination together. We look after the details so the people in the room can be present for the moment.",
    about_mission_title: "Thoughtful, through and through.",
    about_mission_body: "We believe exceptional events begin with listening. Every concept, venue, supplier and production cue is shaped around the purpose of your gathering and the people it brings together.",
    about_stats: "Creative direction · Event production · Guest experience · New Delhi, India",
};

function useContentBlock(key) {
    const [value, setValue] = useState(null);
    useEffect(() => {
        api.get(`/content/blocks/${key}`).then(r => setValue(r.data?.value ?? null)).catch(() => setValue(null));
    }, [key]);
    return value;
}

export default function About() {
    const heroTitle = useContentBlock("about_hero_title") ?? DEFAULTS.about_hero_title;
    const heroSubtitle = useContentBlock("about_hero_subtitle") ?? DEFAULTS.about_hero_subtitle;
    const heroImage = useContentBlock("about_hero_image") ?? DEFAULTS.about_hero_image;
    const storyTitle = useContentBlock("about_story_title") ?? DEFAULTS.about_story_title;
    const storyBody = useContentBlock("about_story_body") ?? DEFAULTS.about_story_body;
    const missionTitle = useContentBlock("about_mission_title") ?? DEFAULTS.about_mission_title;
    const missionBody = useContentBlock("about_mission_body") ?? DEFAULTS.about_mission_body;
    const stats = useContentBlock("about_stats") ?? DEFAULTS.about_stats;

    return (
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 pb-24" data-testid="about-page">
            <SEO title="About" description={String(heroSubtitle || "").slice(0, 160)} image={heroImage} />
            {/* Hero */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center pt-4">
                <div>
                    <p className="font-mono-ui text-muted-foreground">The house</p>
                    <h1 className="dw-headline text-5xl sm:text-6xl lg:text-7xl mt-4" data-testid="about-hero-title">{heroTitle}</h1>
                    <p className="mt-8 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl" data-testid="about-hero-subtitle">{heroSubtitle}</p>
                </div>
                <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-secondary">
                    <img src={heroImage} alt="" className="absolute inset-0 h-full w-full object-cover" />
                </div>
            </section>

            {/* Story */}
            <section className="mt-24 grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-8 lg:gap-14 items-start">
                <p className="font-mono-ui text-muted-foreground lg:sticky lg:top-32">Chapter 01</p>
                <div>
                    <h2 className="dw-headline text-4xl sm:text-5xl">{storyTitle}</h2>
                    <p className="mt-6 text-muted-foreground leading-relaxed whitespace-pre-line" data-testid="about-story-body">{storyBody}</p>
                </div>
            </section>

            {/* Mission */}
            <section className="mt-24 grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-8 lg:gap-14 items-start">
                <p className="font-mono-ui text-muted-foreground lg:sticky lg:top-32">Chapter 02</p>
                <div>
                    <h2 className="dw-headline text-4xl sm:text-5xl">{missionTitle}</h2>
                    <p className="mt-6 text-muted-foreground leading-relaxed whitespace-pre-line">{missionBody}</p>
                </div>
            </section>

            {/* Stats ribbon */}
            <section className="mt-24 border-y border-border py-8">
                <p className="font-display text-2xl sm:text-3xl text-center" data-testid="about-stats">{stats}</p>
            </section>
        </div>
    );
}
